// Projects — list via POST /projects/all (server-side pagination + Live/Archived
// split). Request body, response fields and enums are confirmed against the
// backend; normalization stays isolated in toProject so any drift is a
// one-file fix.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

// Backend enums (confirmed). The model carries TWO independent columns:
// `status` is the lifecycle (the Live/Archived tab split); `state` is the
// operational flag, independent of the lifecycle status.
export type ProjectStatus = 'active' | 'archived';
export type ProjectState = 'active' | 'inactive';
export type ProjectPriority = 'low' | 'medium' | 'high' | 'urgent';
export type ProjectType = 'public' | 'private' | 'test';
export type ProjectSortKey =
  | 'title'
  | 'status'
  | 'state'
  | 'priority'
  | 'startDate'
  | 'dueDate'
  | 'createdAt'
  | 'updatedAt';
export type SortDirection = 'asc' | 'desc';

/** Curated project-icon library (from the design prototype) — the create/edit
 *  icon grid later; also the sanity set for row tiles. */
export const PROJECT_ICONS = [
  'folder-kanban',
  'flask-conical',
  'shield-check',
  'hard-hat',
  'wrench',
  'clipboard-check',
  'clipboard-list',
  'calendar-check',
  'truck',
  'building-2',
  'warehouse',
  'flame',
  'zap',
  'droplets',
  'leaf',
  'package',
  'map-pin',
  'users',
  'briefcase',
  'target',
  'activity',
  'layers',
  'settings',
  'boxes',
];

export interface ProjectListItem {
  id: string;
  name: string;
  icon: string;
  isDefault: boolean;
  /** Default projects: everyone-access vs restricted (members added explicitly). */
  everyoneAccess: boolean;
  membersCount: number;
  /** Owner-first, same mapping the detail response gets. */
  members: ProjectMember[];
  owner: { id: string; name: string };
  status: ProjectStatus;
  state: ProjectState;
  priority: ProjectPriority;
  startDate: string;
  endDate: string;
  tags: { id: string; title: string }[];
}

export interface ProjectsPage {
  items: ProjectListItem[];
  /** Regular (paginated) projects only — the pinned defaults are extra. */
  total: number;
  /** The system default projects — pinned rows, outside pagination; the
   *  backend sends them only on the first page's response. */
  defaults: ProjectListItem[];
}

/** A project member (or the owner) as the detail response carries them: `id` is
 *  the company-member id — it's what `createdBy._id` matches. */
export interface ProjectMember {
  id: string;
  name: string;
}

export interface ProjectGroup {
  id: string;
  name: string;
}

/** GET /projects/detail/:id — mirrors ProjectListItem's naming, plus the fields
 *  only the detail response carries (description, members, groups) and the two
 *  counts the KPI row can honestly show. */
export interface ProjectDetail {
  id: string;
  name: string;
  referenceNumber: number;
  description: string;
  icon: string;
  /** The detail page branches per type: 'public' and 'test' render reduced
   *  variants; 'private' is the full page. */
  type: ProjectType;
  isDefault: boolean;
  everyoneAccess: boolean;
  owner: { id: string; name: string };
  status: ProjectStatus;
  state: ProjectState;
  priority: ProjectPriority;
  startDate: string;
  endDate: string;
  tags: { id: string; title: string }[];
  /** Owner-first, matching the design's member list. */
  members: ProjectMember[];
  groups: ProjectGroup[];
  taskCount: number;
  inspectionCount: number;
  createdAt: string;
  updatedAt: string;
}

/** POST /projects/create body. members (user ids) + groups (group ids) come
 *  from the drawer's Members picker; dates are ISO datetimes (both required
 *  by the form); status is backend-defaulted. */
export interface CreateProjectPayload {
  title: string;
  description: string;
  members: string[];
  groups: string[];
  priority: ProjectPriority;
  icon: string;
  startDate: string;
  dueDate: string;
  tags: string[];
}

/** PATCH /projects/update/:id body — confirmed as the create payload with every
 *  field optional. Callers send ONLY the keys they changed, so an edit screen
 *  can't clobber fields it doesn't own (e.g. `icon`, `groups`). */
export type UpdateProjectPayload = Partial<CreateProjectPayload>;

/** PATCH /projects/update/:id/state body — the operational `state` column only,
 *  never the lifecycle `status`. `endDate` rides along when activating;
 *  deactivating sends the state alone.
 *
 *  The field really is `endDate` on THIS route (confirmed), not the `dueDate`
 *  the create/update bodies use for the same date. Don't align it with them. */
export interface UpdateProjectStatePayload {
  state: ProjectState;
  endDate?: string;
}

/** POST /projects/all body — page/limit required; the rest are the filter/sort
 *  params. `status` is REQUIRED and STRICT (one status per request): the Live
 *  tab sends 'active', the Archived tab sends 'archived'. */
export interface ProjectsQueryArgs {
  status: ProjectStatus;
  page: number;
  limit: number;
  /** Multi-select filter arrays (confirmed) — omitted when empty. */
  priority?: ProjectPriority[];
  tags?: string[];
  state?: ProjectState[];
  members?: string[];
  search?: string;
  orderBy?: { key: ProjectSortKey; type: SortDirection };
}

// ---- raw shapes (strict per the confirmed response) ----
interface ApiProjectUser {
  _id: string;
  user: { _id: string; firstName?: string; lastName?: string };
}
interface ApiProject {
  _id: string;
  title: string;
  description: string;
  icon: string;
  /** 'public'/'test' are the two system defaults; user-created = 'private'. */
  projectType: ProjectType;
  createdBy: ApiProjectUser;
  status: ProjectStatus;
  state: ProjectState;
  priority: ProjectPriority;
  /** Null on the default projects. */
  startDate: string | null;
  dueDate: string | null;
  tags: Array<{ _id: string; title: string }>;
  /** Same populated shape as the detail's members (confirmed 2026-10-02). */
  members: ApiProjectUser[];
}
/** `defaultProject` (the pinned system projects) rides only on the first
 *  page's response — later pages omit it. */
interface ApiProjectsPage {
  items: ApiProject[];
  totalRecords: number;
  defaultProject?: ApiProject[];
}

// 'public' and 'test' projects are the system defaults (locked rows, DEFAULT badge).
const DEFAULT_TYPES = new Set<ProjectType>(['public', 'test']);

const creatorName = (u: ApiProjectUser): string =>
  `${u.user.firstName ?? ''} ${u.user.lastName ?? ''}`.trim();

// The owner rides along inside `members`; the design lists them first. Shared
// by the list and detail mappers — both responses carry the same member shape.
const toMembers = (members: ApiProjectUser[], ownerId: string): ProjectMember[] =>
  members
    .map((m) => ({ id: m._id, name: creatorName(m) }))
    .sort((a, b) => Number(b.id === ownerId) - Number(a.id === ownerId));

const toProject = (p: ApiProject): ProjectListItem => ({
  id: p._id,
  name: p.title,
  icon: p.icon,
  isDefault: DEFAULT_TYPES.has(p.projectType),
  // 'public' system project = everyone-access; 'test' = restricted.
  everyoneAccess: p.projectType === 'public',
  membersCount: p.members.length,
  members: toMembers(p.members, p.createdBy._id),
  owner: { id: p.createdBy._id, name: creatorName(p.createdBy) },
  status: p.status,
  state: p.state,
  priority: p.priority,
  startDate: p.startDate ?? '',
  endDate: p.dueDate ?? '',
  tags: p.tags.map((t) => ({ id: t._id, title: t.title })),
});

/** GET /projects/detail/:id raw shape (strict per the confirmed response). The
 *  `members` entries share `createdBy`'s exact shape, so `ApiProjectUser` and
 *  `creatorName` are reused for both. */
interface ApiProjectDetail {
  _id: string;
  company: string;
  title: string;
  referenceNumber: number;
  description: string;
  projectType: ProjectType;
  createdBy: ApiProjectUser;
  members: ApiProjectUser[];
  groups: Array<{ _id: string; groupName: string }>;
  status: ProjectStatus;
  state: ProjectState;
  priority: ProjectPriority;
  icon: string;
  startDate: string | null;
  dueDate: string | null;
  tags: Array<{ _id: string; title: string }>;
  /** Confirmed present, but empty in every sample so far — the element shape is
   *  unknown, so only `.length` is consumed (the Tasks/Inspections KPI counts). */
  tasks: unknown[];
  inspections: unknown[];
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const toProjectDetail = (p: ApiProjectDetail): ProjectDetail => {
  const ownerId = p.createdBy._id;
  const members = toMembers(p.members, ownerId);

  return {
    id: p._id,
    name: p.title,
    referenceNumber: p.referenceNumber,
    description: p.description,
    icon: p.icon,
    type: p.projectType,
    isDefault: DEFAULT_TYPES.has(p.projectType),
    everyoneAccess: p.projectType === 'public',
    owner: { id: ownerId, name: creatorName(p.createdBy) },
    status: p.status,
    state: p.state,
    priority: p.priority,
    startDate: p.startDate ?? '',
    endDate: p.dueDate ?? '',
    tags: p.tags.map((t) => ({ id: t._id, title: t.title })),
    members,
    groups: p.groups.map((g) => ({ id: g._id, name: g.groupName })),
    taskCount: p.tasks.length,
    inspectionCount: p.inspections.length,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  };
};

/** Picker option from GET /projects/selection — `projectType: 'public'` marks
 *  the company's public project (used as the default pick). */
export interface ProjectOption {
  id: string;
  name: string;
  projectType: string;
  /** kebab-case lucide name. */
  icon: string;
}

export const projectsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getProjects: build.query<ProjectsPage, ProjectsQueryArgs>({
      query: (body) => ({ ...ROUTES.projects.list, data: body }),
      transformResponse: (res: ApiProjectsPage) => ({
        items: res.items.map(toProject),
        total: res.totalRecords,
        defaults: (res.defaultProject ?? []).map(toProject),
      }),
      providesTags: [{ type: 'Projects', id: 'LIST' }],
    }),
    // Lightweight project picker source (GET /projects/selection) — the
    // response is already normalized ({id, title, projectType, icon}).
    getProjectsSelection: build.query<ProjectOption[], void>({
      query: () => ({ ...ROUTES.projects.selection }),
      transformResponse: (
        res: Array<{ id: string; title: string; projectType: string; icon: string }>,
      ) => res.map((p) => ({ id: p.id, name: p.title, projectType: p.projectType, icon: p.icon })),
      providesTags: [{ type: 'Projects', id: 'SELECTION' }],
    }),
    getProjectDetail: build.query<ProjectDetail, string>({
      query: (id) => ({ ...ROUTES.projects.detail(id) }),
      transformResponse: toProjectDetail,
      providesTags: (_res, _err, id) => [{ type: 'Projects', id }],
    }),
    createProject: build.mutation<{ _id?: string }, CreateProjectPayload>({
      query: (body) => ({ ...ROUTES.projects.create, data: body }),
      invalidatesTags: [
        { type: 'Projects', id: 'LIST' },
        { type: 'Projects', id: 'SELECTION' },
      ],
    }),
    // Invalidates the detail, the list AND the pickers, so a rename/priority
    // change shows up everywhere without a manual refetch — `title` is the
    // picker's own label, so SELECTION goes stale on a rename too.
    updateProject: build.mutation<unknown, { id: string; body: UpdateProjectPayload }>({
      query: ({ id, body }) => ({ ...ROUTES.projects.update(id), data: body }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Projects', id },
        { type: 'Projects', id: 'LIST' },
        { type: 'Projects', id: 'SELECTION' },
      ],
    }),
    /** PATCH /projects/update/:id/state — one project's operational flag. Its
     *  own endpoint, so the Settings tab's Status card saves independently of
     *  the General draft's PATCH. SELECTION is deliberately NOT invalidated:
     *  the picker payload is {id, title, projectType, icon}, which `state`
     *  cannot change. */
    updateProjectState: build.mutation<unknown, { id: string; body: UpdateProjectStatePayload }>({
      query: ({ id, body }) => ({ ...ROUTES.projects.updateState(id), data: body }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Projects', id },
        { type: 'Projects', id: 'LIST' },
      ],
    }),
    /** POST /projects/update/status — bulk lifecycle move: every id gets the
     *  one status ('archived' to archive, 'active' to restore). SELECTION is
     *  invalidated because archiving may remove a project from the pickers (and
     *  restoring may put it back); harmless if the backend doesn't filter them. */
    updateProjectsStatus: build.mutation<unknown, { ids: string[]; status: ProjectStatus }>({
      query: (body) => ({ ...ROUTES.projects.updateStatus, data: body }),
      invalidatesTags: (_res, _err, { ids }) => [
        { type: 'Projects' as const, id: 'LIST' },
        { type: 'Projects' as const, id: 'SELECTION' },
        ...ids.map((id) => ({ type: 'Projects' as const, id })),
      ],
    }),
    /** DELETE /projects/delete/:id — one project, permanently. Only reachable
     *  on an ARCHIVED project (product rule), so the listing offers it on the
     *  Archived tab only and the Danger Zone keeps its button disabled until
     *  then. Invalidates the LIST and the pickers but deliberately NOT the
     *  `{Projects, id}` tag: that would refetch the detail of a project that
     *  no longer exists (the rolesApi rule). */
    deleteProject: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.projects.remove(id) }),
      invalidatesTags: [
        { type: 'Projects', id: 'LIST' },
        { type: 'Projects', id: 'SELECTION' },
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectsSelectionQuery,
  useGetProjectDetailQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useUpdateProjectStateMutation,
  useUpdateProjectsStatusMutation,
  useDeleteProjectMutation,
} = projectsApi;
